import {
  doc,
  getDoc,
  getCountFromServer,
  query,
  where,
  writeBatch,
  serverTimestamp,
  arrayUnion,
} from 'firebase/firestore';
import { db } from '../../config';
import { groupsRef } from './read';
import { usersRef } from '../user/read';
import { groupStatus, groupLimit } from '../../../../constants/enum';
import { groupMessages } from '../../../../constants/messages';
import { ValidationError } from '../../../../modules/error';
import type { GroupFormValues } from '../../../../types/group';

/**
 * グループを作成する（#2804）
 * - 企業内の有効グループ数が上限(50)未満であることを確認
 * - 指定された所属メンバー（memberIds）を member_ids に設定し、
 *   各ユーザーの users.group_ids にも追加（同一バッチで双方向同期）
 * - いずれかのメンバーが所属上限(10)に達している場合はエラー
 * @returns 作成したグループID
 */
export const createGroup = async (
  companyId: string,
  values: GroupFormValues,
  memberIds: string[],
  createdBy: string,
): Promise<string> => {
  // グループ数上限チェック
  const countSnapshot = await getCountFromServer(
    query(groupsRef(companyId), where('status', '==', groupStatus.valid)),
  );
  if (countSnapshot.data().count >= groupLimit.maxGroupsPerCompany) {
    throw new ValidationError(groupMessages.companyLimitReached(groupLimit.maxGroupsPerCompany));
  }

  // メンバーの所属上限チェック
  if (memberIds.length > 0) {
    const userSnapshots = await Promise.all(memberIds.map(uid => getDoc(doc(usersRef, uid))));
    for (const userSnapshot of userSnapshots) {
      const currentGroupIds: string[] = userSnapshot.data()?.group_ids ?? [];
      if (currentGroupIds.length >= groupLimit.maxGroupsPerUser) {
        throw new ValidationError(groupMessages.userLimitReached(groupLimit.maxGroupsPerUser));
      }
    }
  }

  const batch = writeBatch(db);
  const now = serverTimestamp();

  // 1. グループ作成
  const groupDocRef = doc(groupsRef(companyId));
  batch.set(groupDocRef, {
    name: values.name,
    description: values.description ?? null,
    member_ids: memberIds,
    status: groupStatus.valid,
    created_at: now,
    updated_at: now,
    created_by: createdBy,
    updated_by: createdBy,
  });

  // 2. 各メンバーの users.group_ids に追加（双方向同期）
  memberIds.forEach(uid => {
    batch.update(doc(usersRef, uid), { group_ids: arrayUnion(groupDocRef.id) });
  });

  await batch.commit();
  return groupDocRef.id;
};
